import { inView, animate, stagger } from "motion";
import isMobile from "./isMobile";

/**
 * Scroll Reveal Implementation
 * - Phase 1: Classify all elements with .reveal-item (body still hidden).
 * - Phase 2: Reveal body, then set up inView observers after transition ends
 *   to prevent animations firing while the body is still invisible.
 */
export default function initScrollReveal() {

  // Selectors for containers that group their children's animations.
  const containerSelectors = [
    '.paragraph',
    '.block',
    '.view-content',
    '.layout-content',
    'section',
    'article',
    '.footer'
  ];

  // Selectors for individual elements that should animate.
  const contentSelectors = [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    '.text-h2', '.text-h3', '.text-h4',
    'p', 'ul', 'ol', 'cite', 'img:not(.js-parallax)', 'video',
    '.button', 'a', 'form', '.project__company-description',
    '.title-area__content', '.project__header-title-wrapper', '.article__header-title-wrapper', '.field--name-body > *',
    '.service__headline', '.service__description',
    '.poster', '.accordion-wrapper', '.card',
    '.media', '.view-row',
    '.footer__logo', '.article__overview-link', '.project__overview-link',
    '.article-teaser__content', '.article-teaser__image', '.article__header-image', '.locked-content__wrapper',
    '.collage__counter', '.key-facts', '.about-me__hello-text', '.menu-item'
  ];

  // ─── Phase 1: Classify elements ──────────────────────────────────────────
  // Store items per container so we can set up observers in Phase 2.
  const containerItems = [];

  const containers = document.querySelectorAll(containerSelectors.join(', '));
  containers.forEach((container) => {
    const items = container.querySelectorAll(contentSelectors.join(', '));

    let directItems = Array.from(items).filter(item => {
      const nearestContainer = item.parentElement.closest(containerSelectors.join(', '));
      return nearestContainer === container;
    });

    // On mobile: animate individual .menu-item instead of the .menu wrapper.
    if (isMobile()) {
      directItems = directItems.filter(item => !(item.classList.contains('menu') && item.closest('.header')));
    }

    // Remove descendants of other animating items to prevent double animations.
    directItems = directItems.filter(item => {
      return !directItems.some(ancestor => ancestor !== item && ancestor.contains(item));
    });

    if (directItems.length === 0) return;

    // Mark elements — CSS uses this to hide them immediately.
    directItems.forEach(item => item.classList.add('reveal-item'));
    containerItems.push({ container, directItems });
  });

  // Classify standalone elements not inside any container.
  let allLeafs = Array.from(document.querySelectorAll(contentSelectors.join(', ')));

  if (isMobile()) {
    allLeafs = allLeafs.filter(leaf => !(leaf.classList.contains('menu') && leaf.closest('.header')));
  }

  allLeafs = allLeafs.filter(leaf => {
    return !allLeafs.some(ancestor => ancestor !== leaf && ancestor.contains(leaf));
  });

  const leafItems = allLeafs.filter(leaf => !leaf.classList.contains('reveal-item'));
  leafItems.forEach(leaf => leaf.classList.add('reveal-item'));

  // ─── Phase 2: Reveal body, then set up observers ─────────────────────────
  // Adding scroll-reveal-ready triggers the CSS body opacity transition.
  // We only set up inView observers AFTER the transition completes so that
  // IntersectionObserver doesn't fire while the body is still invisible.
  document.documentElement.classList.add('scroll-reveal-ready');

  function setupObservers() {
    const staggerConfig = {
      delay: stagger(0.08, { start: 0.05 }),
      duration: 0.7,
      easing: [0.22, 1, 0.36, 1] // OutQuint
    };
    const itemConfig = { duration: 0.7, easing: [0.22, 1, 0.36, 1] };
    const animationStyles = { opacity: [0, 1], y: [30, 0] };

    containerItems.forEach(({ container, directItems }) => {
      // On mobile, header items animate immediately (fixed position breaks inView).
      if (isMobile() && container.closest('.header')) {
        animate(directItems, animationStyles, staggerConfig);
      } else {
        inView(container, () => {
          animate(directItems, animationStyles, staggerConfig);
        }, { margin: "0px 0px -10% 0px" });
      }
    });

    leafItems.forEach(leaf => {
      if (isMobile() && leaf.closest('.header')) {
        animate(leaf, animationStyles, itemConfig);
      } else {
        inView(leaf, () => {
          animate(leaf, animationStyles, itemConfig);
        }, { margin: "0px 0px -5% 0px" });
      }
    });
  }

  // Wait for the body opacity transition to finish before observing.
  document.body.addEventListener('transitionend', function handler(e) {
    if (e.propertyName !== 'opacity' || e.target !== document.body) return;
    document.body.removeEventListener('transitionend', handler);
    setupObservers();
  });
}
